import React, { useState, useEffect, useCallback } from "react";
import { Typography, Grid, Card, Avatar, Button, Box, Divider } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import CircularLoading from "../components/CircularLoading";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AlertDialog from "../components/AlertDialog";
import { api } from "common";
import { MAIN_COLOR, SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { makeStyles } from "@mui/styles";
import GoBackButton from "components/GoBackButton";
import BadgeIcon from "@mui/icons-material/Badge";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BusinessIcon from "@mui/icons-material/Business";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "10px",
    backgroundColor: SECONDORY_COLOR,
    minHeight: 60,
    minWidth: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 2,
    marginBottom: 10,
    boxShadow: `0px 2px 5px ${MAIN_COLOR}`,
  },
  avatar: {
    width: 300,
    height: 250,
    display: "flex",
    flexDirection: "column",
    boxShadow: 3,
    border: `2px dashed ${colors.TABLE_BORDER}`,
    fontSize: 16,
    background: "none",
    color: "inherit",
    fontWeight: "bold",
  },
  buttonStyle: {
    borderRadius: "19px",
    minHeight: 50,
    minWidth: 150,
    marginBottom: 20,
    marginTop: 20,
    textAlign: "center",
    cursor: "pointer",
    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
    fontFamily: FONT_FAMILY,
    "&:hover": {
      backgroundColor: MAIN_COLOR,
    },
  }
}));

const EMPTY_IMAGES = {
  verifyIdImage: null,
  licenseImage: null,
  licenseImageBack: null,
  idImage: null,
  idImageBack: null,
  vtcCard: null,
  vtcCardBack: null,
  vtcSticker: null,
  vtcRegistry: null,
  medicalCertificate: null,
  kbis: null,
  vehicleInsurance: null,
  rcPro: null,
  vehicleRegistration: null,
  technicalInspection: null
};

function UserDocuments() {
  const { id, rId } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const auth = useSelector((state) => state.auth);
  const role = auth?.profile?.usertype;
  const { fetchUsersOnce, updateLicenseImage } = api;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editable, setEditable] = useState(false);
  const [images, setImages] = useState(EMPTY_IMAGES);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const classes = useStyles();

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      const user = staticusers.find((user) => user.id === id.toString());
      if (!user) {
        navigate("/404");
      } else {
        setData(user);
      }
    }
  }, [staticusers, id, navigate]);

  const handleImageChange = useCallback((field) => (e) => {
    setImages(prev => ({ ...prev, [field]: e.target.files[0] }));
  }, []);

  const handleSaveUser = useCallback(async () => {
    setLoading(true);

    try {
      // Admin peut toujours modifier, sinon vérifier AllowCriticalEditsAdmin
      if (!(role === "admin" || settings.AllowCriticalEditsAdmin)) {
        setCommonAlert({ open: true, msg: t("demo_mode") });
        return;
      }

      const uploads = Object.entries(images).filter(([, image]) => !!image);
      if (uploads.length === 0) {
        setCommonAlert({ open: true, msg: t("no_doc_uploaded") });
        return;
      }

      await Promise.all(
        uploads.map(([field, image]) => dispatch(updateLicenseImage(data.id, image, field)))
      );

      setImages(EMPTY_IMAGES);
      setEditable(false);
      await dispatch(fetchUsersOnce());
      setCommonAlert({ open: true, msg: "Documents mis à jour avec succès" });
    } catch (error) {
      console.error("UserDocuments upload error:", error);
      setCommonAlert({ open: true, msg: error?.message || "Échec de l'upload des documents" });
    } finally {
      setLoading(false);
    }
  }, [role, settings.AllowCriticalEditsAdmin, images, data.id, dispatch, t, fetchUsersOnce, updateLicenseImage]);

  const handleCancel = useCallback(() => {
    setImages(EMPTY_IMAGES);
    setEditable(false);
  }, []);

  const commonFields = [
    { 
      title: t("verifyid_image"), 
      imageUrl: data?.verifyIdImage, 
      placeholder: t("verifyid_image"), 
      field: "verifyIdImage",
      category: "identity"
    },
  ];

  const identityFields = [
    // Permis de conduire
    { 
      title: t("license_image_front"), 
      imageUrl: data?.licenseImage, 
      placeholder: t("license_image_front"), 
      field: "licenseImage",
      category: "identity"
    },
    { 
      title: t("license_image_back"), 
      imageUrl: data?.licenseImageBack, 
      placeholder: t("license_image_back"), 
      field: "licenseImageBack",
      category: "identity"
    },
  ];

  const professionalFields = [
    // Carte VTC
    { 
      title: "Carte VTC (recto)", 
      imageUrl: data?.vtcCard, 
      placeholder: "Carte VTC (recto)", 
      field: "vtcCard",
      category: "professional"
    },
    { 
      title: "Carte VTC (verso)", 
      imageUrl: data?.vtcCardBack, 
      placeholder: "Carte VTC (verso)", 
      field: "vtcCardBack",
      category: "professional"
    },
    // Pièce d'identité
    { 
      title: "Pièce d'identité (recto)", 
      imageUrl: data?.idImage, 
      placeholder: "Pièce d'identité (recto)", 
      field: "idImage",
      category: "identity"
    },
    { 
      title: "Pièce d'identité (verso)", 
      imageUrl: data?.idImageBack, 
      placeholder: "Pièce d'identité (verso)", 
      field: "idImageBack",
      category: "identity"
    },
    // Macaron VTC
    { 
      title: "Macaron VTC", 
      imageUrl: data?.vtcSticker, 
      placeholder: "Macaron VTC", 
      field: "vtcSticker",
      category: "professional"
    },
    // Certificat de registre VTC
    { 
      title: "Certificat de registre VTC", 
      imageUrl: data?.vtcRegistry, 
      placeholder: "Certificat de registre VTC", 
      field: "vtcRegistry",
      category: "professional"
    },
    // Certificat médical professionnel
    { 
      title: "Certificat de visite médicale professionnelle", 
      imageUrl: data?.medicalCertificate, 
      placeholder: "Certificat de visite médicale professionnelle", 
      field: "medicalCertificate",
      category: "professional"
    },
    // K-bis
    { 
      title: "K-bis", 
      imageUrl: data?.kbis, 
      placeholder: "K-bis", 
      field: "kbis",
      category: "professional"
    },
    // RC PRO
    { 
      title: "RC Professionnelle", 
      imageUrl: data?.rcPro, 
      placeholder: "RC Professionnelle", 
      field: "rcPro",
      category: "professional"
    },
  ];

  const vehicleFields = [
    // Assurance véhicule
    { 
      title: "Assurance véhicule", 
      imageUrl: data?.vehicleInsurance, 
      placeholder: "Assurance véhicule", 
      field: "vehicleInsurance",
      category: "vehicle"
    },
    // Carte grise
    { 
      title: "Carte grise", 
      imageUrl: data?.vehicleRegistration, 
      placeholder: "Carte grise", 
      field: "vehicleRegistration",
      category: "vehicle"
    },
    // Contrôle technique
    { 
      title: "Contrôle technique (optionnel)", 
      imageUrl: data?.technicalInspection, 
      placeholder: "Contrôle technique", 
      field: "technicalInspection",
      category: "vehicle"
    },
  ];

  const driverFields = [...identityFields, ...professionalFields, ...vehicleFields];

  const imageFields = data.usertype === "driver" ? [...commonFields, ...driverFields] : commonFields;

  const CategorySection = ({ title, icon, fields }) => (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        {icon}
        <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: FONT_FAMILY, 
            fontWeight: 'bold', 
            color: MAIN_COLOR,
            fontSize: '20px'
          }}
        >
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 3, borderColor: MAIN_COLOR, borderWidth: 1 }} />
      <Grid container spacing={3} justifyContent="flex-start">
        {editable ? (
          fields?.map((field, index) => (
            <Grid item key={index}>
              <Card className={classes.card}>
                <Typography style={{ textAlign: "center", fontSize: 16, fontWeight: "bold", fontFamily: FONT_FAMILY }}>
                  {field.title}
                </Typography>
              </Card>
              <ImageUpload 
                image={images[field.field]} 
                onClick={() => document.getElementById(field.field).click()} 
                altText={field.title} 
                uploadText={field.placeholder} 
              />
            </Grid>
          ))
        ) : (
          fields?.map((field, index) => (
            <ImageCard 
              key={index} 
              title={field.title} 
              imageUrl={field.imageUrl} 
              placeholder={field.placeholder} 
              onClick={() => setEditable(true)} 
            />
          ))
        )}
      </Grid>
    </Box>
  );

  const ImageUpload = ({ image, onClick, altText, uploadText }) => (
    image ? (
      <div onClick={onClick} style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
        <img src={URL.createObjectURL(image)} alt={altText} style={{ width: 300, height: 250, borderRadius: "19px" }} />
      </div>
    ) : (
      <div onClick={onClick} style={{ display: "flex", justifyContent: "center", marginTop: 30, cursor: "pointer" }}>
        <Avatar className={classes.avatar} sx={{ boxShadow: 3 }} variant="square">
          <FileUploadIcon sx={{ fontSize: 100, marginBottom: 3, color: colors.Header_Text_back, fontFamily: FONT_FAMILY }} />
          <Typography sx={{ textAlign: "center" }} fontFamily={FONT_FAMILY}>{uploadText}</Typography>
        </Avatar>
      </div>
    )
  );

  const ImageCard = ({ title, imageUrl, placeholder, onClick }) => (
    <Grid item>
      <Card className={classes.card}>
        <Typography style={{ textAlign: "center", fontSize: 16, fontWeight: "bold", fontFamily: FONT_FAMILY }}>
          {title}
        </Typography>
      </Card>
      <Grid item>
        {imageUrl ? (
          <Avatar alt={title} src={imageUrl} sx={{ width: 300, height: 250, borderRadius: "19px", cursor: "pointer" }} variant="square" onClick={onClick} />
        ) : (
          <Avatar className={classes.avatar} variant="square" sx={{ boxShadow: 3, cursor: "pointer", fontFamily: FONT_FAMILY, textAlign: "center" }} onClick={onClick}>
            {placeholder}
          </Avatar>
        )}
      </Grid>
    </Grid>
  );

  const EditButtonGroup = ({ editable, onEdit, onSave, onCancel }) => (
    <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
      {!editable ? (
        <Button
          className={classes.buttonStyle}
          sx={{
            backgroundColor: MAIN_COLOR,
            width: "50%",
            cursor: "pointer",
            borderColor: colors.CARD_DETAIL,
          }}
          variant="contained"
          onClick={onEdit}
        >
          <Typography sx={{ textAlign: "center", fontSize: 16, fontWeight: "bold", fontFamily: FONT_FAMILY, color: colors.WHITE }}>
            {t("edit")}
          </Typography>
        </Button>
      ) : (
        <>
          <Button
            className={classes.buttonStyle}
            sx={{ backgroundColor: colors.GREEN, width: "40%", "&:hover": { backgroundColor: MAIN_COLOR } }}
            variant="contained"
            onClick={onSave}
          >
            <Typography sx={{ color: colors.WHITE, textAlign: "center", fontSize: 16 }}>{t("save")}</Typography>
          </Button>
          <Button className={classes.buttonStyle} sx={{ backgroundColor: colors.RED, width: "40%" }} variant="contained" onClick={onCancel}>
            <Typography sx={{ color: colors.WHITE, textAlign: "center", fontSize: 16 }}>{t("cancel")}</Typography>
          </Button>
        </>
      )}
    </div>
  );

  return loading ? (
    <CircularLoading />
  ) : (
    <Card sx={{ borderRadius: "19px", backgroundColor: colors.WHITE, minHeight: 200, marginTop: 1, marginBottom: 1, padding: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", justifyContent: isRTL === "rtl" ? "flex-end" : "flex-start" }}>
        <Typography variant="h5" sx={{ margin: "10px 10px 0 5px", fontFamily: FONT_FAMILY }}>{t("documents_title")}</Typography>
        <GoBackButton isRTL={isRTL} onClick={() => navigate(`/users/${rId}`, { state: { pageNo: state?.pageNo } })} />
      </div>
      <Grid container spacing={1} direction="column" sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", px: 3 }}>
        <Grid item sx={{ width: '100%', maxWidth: '1400px' }}>
          {/* Documents Identité */}
          {commonFields.length > 0 && (
            <CategorySection 
              title="Documents d'identité"
              icon={<BadgeIcon sx={{ fontSize: 28, color: MAIN_COLOR }} />}
              fields={[...commonFields, ...(data.usertype === "driver" ? identityFields : [])]}
            />
          )}
          
          {/* Documents Professionnels - Chauffeur uniquement */}
          {data.usertype === "driver" && professionalFields.length > 0 && (
            <CategorySection 
              title="Documents professionnels"
              icon={<BusinessIcon sx={{ fontSize: 28, color: MAIN_COLOR }} />}
              fields={professionalFields}
            />
          )}
          
          {/* Documents Véhicule - Chauffeur uniquement */}
          {data.usertype === "driver" && vehicleFields.length > 0 && (
            <CategorySection 
              title="Documents véhicule"
              icon={<DirectionsCarIcon sx={{ fontSize: 28, color: MAIN_COLOR }} />}
              fields={vehicleFields}
            />
          )}
        </Grid>
        
        <EditButtonGroup editable={editable} onEdit={() => setEditable(true)} onSave={handleSaveUser} onCancel={handleCancel} />
      </Grid>
      {imageFields?.map((field) => (
        <input key={field.field} id={field.field} type="file" accept="image/*" hidden onChange={handleImageChange(field.field)} />
      ))}
      <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
        {commonAlert.msg}
      </AlertDialog>
    </Card>
  );
}

export default UserDocuments;
